import { Badge } from '@/components/common/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent } from '@/components/common/tabs'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { fromUnixTimestamp } from '@vaultsfyi/common/utils'
import { formatDate, formatTokenValue } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import type { useForm } from 'react-hook-form'
import { type Address, formatUnits } from 'viem'
import { z } from 'zod'
import { TxSteps } from '../sidePanel/txSteps'

const ClaimStatus = {
  READY: 'ready',
  PENDING: 'pending',
} as const

const claimDataSchema = z.array(
  z.object({
    id: z.string(),
    assetAmount: z.string(),
    lpTokenAmount: z.string(),
    timestamp: z.coerce.number().optional(),
    status: z.nativeEnum(ClaimStatus),
  })
)

interface Props {
  account: Address | undefined
  stepData: StepData
  onSubmit: () => void
  isSubmitLoading: boolean
  form: ReturnType<typeof useForm>
  vault: Vault
  transactionsContext: TransactionsContext
  currentStep: number
}

export function LidoWithdrawalTab({
  account,
  stepData,
  onSubmit: _onSubmit,
  isSubmitLoading,
  form,
  vault,
  transactionsContext,
  currentStep,
}: Props) {
  const claimData = useMemo(() => {
    return claimDataSchema.parse((transactionsContext?.vaultSpecificData as Record<string, any>)['claimData'])
  }, [transactionsContext])

  const onSubmit = () => {
    const claimValue = claimData.reduce((acc, data) => {
      if (data.status === ClaimStatus.PENDING) return acc
      return acc + BigInt(data.lpTokenAmount)
    }, 0n)
    form.setValue('amount', formatUnits(claimValue, vault.asset.decimals), { shouldValidate: true })
    _onSubmit()
  }

  const hasTimestamps = !claimData.some((data) => !data.timestamp)
  const isClaimDataEmpty = claimData.length === 0
  const isClaimable = claimData.some((data) => data.status === ClaimStatus.READY)
  const isClaimDisabled = isClaimDataEmpty || !isClaimable

  return (
    <form onSubmit={onSubmit}>
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        {isClaimDataEmpty ? (
          <p className="text-xs">No claimable withdrawals</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow variant="header">
                <TableHead>Amount</TableHead>
                {hasTimestamps && <TableHead>Date</TableHead>}
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {claimData.map((data) => (
                <TableRow key={data.id}>
                  <TableCell>
                    {formatTokenValue({ value: data.assetAmount, decimals: vault.asset.decimals })} {vault.asset.symbol}
                  </TableCell>
                  {hasTimestamps && (
                    <TableCell>
                      {formatDate({
                        timestamp: fromUnixTimestamp(data.timestamp ?? 0),
                        showYear: true,
                        showTime: true,
                      })}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge color={data.status === 'pending' ? 'orange' : 'green'}>{data.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TxSteps
          account={account}
          stepData={stepData}
          activeStep={currentStep}
          disabled={isClaimDisabled}
          loading={isSubmitLoading}
          label="Claim withdrawal"
          network={vault.network.name}
        />
      </TabsContent>
    </form>
  )
}
